/**
 * Phase 22 Plan 22-03 — RemoveSiteConfirmDialog
 *
 * Confirms removal of a single site from a Work Order.
 * On confirm → POST /api/workorders/:id/sites/remove.
 * On 409 (entries reference this site) surfaces the error inline.
 */

import { useState, type JSX } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useRemoveSitesMutation } from '@/hooks/useWorkOrderQueries';
import { toast } from '@/lib/toast';
import type { WorkOrderDetail, WorkOrderSiteItem } from '@/types/work-orders';

interface RemoveSiteConfirmDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly workOrder: WorkOrderDetail;
  readonly site: WorkOrderSiteItem;
}

export function RemoveSiteConfirmDialog({
  open,
  onOpenChange,
  workOrder,
  site,
}: RemoveSiteConfirmDialogProps): JSX.Element {
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const removeMutation = useRemoveSitesMutation();

  function handleConfirm(): void {
    setErrorMsg(null);
    removeMutation.mutate(
      { id: workOrder.id, body: { siteIds: [site.siteId] } },
      {
        onSuccess: () => {
          toast.success(`Site "${site.siteName}" removed`);
          onOpenChange(false);
        },
        onError: (err) => {
          const msg = err.detail ?? err.message;
          setErrorMsg(msg);
          toast.error('Cannot remove site', { description: msg });
        },
      },
    );
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="remove-site-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Remove Site</AlertDialogTitle>
          <AlertDialogDescription>
            Remove <strong>{site.siteName}</strong> from Work Order{' '}
            <strong>{workOrder.woNumber}</strong>? This cannot be undone if logsheet entries
            reference this site.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {errorMsg !== null && (
          <p
            className="text-sm text-destructive px-1"
            data-testid="remove-site-error"
          >
            {errorMsg}
          </p>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={removeMutation.isPending}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); handleConfirm(); }}
            disabled={removeMutation.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="remove-site-confirm-btn"
          >
            {removeMutation.isPending ? 'Removing…' : 'Remove'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
